import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  AlertTriangle,
  Building2,
  CheckCircle2,
  CircleDot,
  Clock3,
  Download,
  Image as ImageIcon,
  Inbox,
  LoaderCircle,
  MessageSquare,
  Paperclip,
  Plus,
  Send,
  Ticket,
  User,
  Users,
  X,
} from "lucide-react";
import { useAuth } from "../../auth/AuthContext";
import NavbarUtility from "./NavbarUtility";

const GOLD = "#ebb51b";
const API_URL = "https://dashboard.technoparts.it/ws/ticket.php";

const PUSH_SUBSCRIBE_URL =
  "https://dashboard.technoparts.it/ws/push/subscribe.php";

const VAPID_PUBLIC_KEY =
  "BFtH8HNil9kgjFUoZCfSCrdvaNxwGWccxLtCkzK7TMHJGpt4u_v6Fn6pk_k-_XYbHKQ462S6cbtdQJrSeF2ygr0";

const urlBase64ToUint8Array = (base64String) => {
  const padding =
    "=".repeat((4 - (base64String.length % 4)) % 4);

  const base64 = (base64String + padding)
    .replace(/-/g, "+")
    .replace(/_/g, "/");

  const rawData = window.atob(base64);

  return Uint8Array.from(
    [...rawData].map((char) => char.charCodeAt(0))
  );
};

const STATUS = {
  OPEN: "aperto",
  WORKING: "in_lavorazione",
  CLOSED: "chiuso",
};

const STATUS_LABELS = {
  [STATUS.OPEN]: "Aperto",
  [STATUS.WORKING]: "In lavorazione",
  [STATUS.CLOSED]: "Chiuso",
};

// Reparti e utenti come da configurazione richiesta.
// Gli username vengono sempre normalizzati in lowercase per combaciare con AuthContext.
const FALLBACK_DEPARTMENTS = {
  IT: ["simone", "federico", "sabri", "dario"],
  MARKETING: ["leandro"],
  VENDITE: ["francesca", "roberta", "emiliano", "bruno"],
  ACQUISTI: ["denise", "rosy", "ilenia", "noemi"],
  CONTABILITÀ: ["viviana", "federica"],
  AMOTIVE: ["emanuele", "daniela", "irene"],
  DIREZIONE: ["alessandro", "rita"],
  "PROCUREMENT SPECIALIST": ["bruna"],
};

// Direzione vede tutti i ticket di tutti i reparti.
const GLOBAL_VIEWERS = new Set(["alessandro", "rita"]);

const normalizeUser = (value) => {
  if (!value) return "guest";

  if (typeof value === "string") {
    return value.trim().toLowerCase();
  }

  if (typeof value === "object") {
    const candidate =
      value.username ||
      value.user ||
      value.name ||
      value.email ||
      value.id ||
      "guest";

    return String(candidate).trim().toLowerCase();
  }

  return String(value).trim().toLowerCase();
};

const displayUser = (username) => {
  if (!username) return "—";

  return String(username)
    .trim()
    .split(/[.\s_-]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join(" ");
};



const findDepartment = (username, departments = FALLBACK_DEPARTMENTS) => {
  const normalized = normalizeUser(username);

  for (const [department, members] of Object.entries(departments)) {
    if (members.includes(normalized)) {
      return department;
    }
  }

  return "NON ASSEGNATO";
};

const parseDbDate = (value) => {
  if (!value) return null;

  const normalized =
    typeof value === "string" && /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}/.test(value)
      ? value.replace(" ", "T")
      : value;

  const date = new Date(normalized);
  return Number.isNaN(date.getTime()) ? null : date;
};

const formatDateTime = (value) => {
  const date = parseDbDate(value);

  if (!date) return "—";

  return date.toLocaleString("it-IT", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const normalizeTicket = (row) => {
  if (!row || typeof row !== "object") return null;

  const urgentRaw = row.urgente;
  const urgent =
    urgentRaw === true ||
    urgentRaw === 1 ||
    urgentRaw === "1" ||
    String(urgentRaw).toLowerCase() === "true";

  const id = Number(row.id_ticket);
  const attachment = row.allegato ? String(row.allegato) : "";
  const replyAttachment = row.allegato_risposta
    ? String(row.allegato_risposta)
    : "";

  return {
    id: Number.isFinite(id) ? id : row.id_ticket,
    code: row.codice_ticket || `#${row.id_ticket ?? "—"}`,
    createdBy: normalizeUser(row.utente_creazione),
    creatorDepartment: row.reparto_creazione || "NON ASSEGNATO",
    targetDepartment: row.reparto_destinatario || "—",
    assignedTo: row.utente_destinatario ? normalizeUser(row.utente_destinatario) : "",
    title: row.titolo || "Ticket senza titolo",
    description: row.descrizione || "",
    urgent,
    status: row.stato || STATUS.OPEN,
    createdAt: row.data_creazione || null,
    updatedAt: row.data_modifica || null,
    statusChangedAt: row.data_cambio_stato || null,
    resolvedAt: row.data_risoluzione || null,
    updatedBy: row.utente_ultima_modifica || null,
    assignedBy: row.assegnato_da || null,
    replyText: row.risposta || "",
    replyBy: row.utente_risposta ? normalizeUser(row.utente_risposta) : null,
    replyAt: row.data_risposta || null,
    image: attachment
      ? {
          dataUrl: attachment,
          name: "Allegato ticket",
        }
      : null,
    replyImage: replyAttachment
      ? {
          dataUrl: replyAttachment,
          name: "Allegato risposta",
        }
      : null,
    raw: row,
  };
};

const sortTickets = (items) =>
  [...items].sort((a, b) => {
    // Prima gli urgenti, poi i più recenti.
    if (a.urgent !== b.urgent) return a.urgent ? -1 : 1;

    const aDate = parseDbDate(a.createdAt)?.getTime() ?? 0;
    const bDate = parseDbDate(b.createdAt)?.getTime() ?? 0;

    if (aDate !== bDate) return bDate - aDate;

    const aId = Number(a.id) || 0;
    const bId = Number(b.id) || 0;
    return bId - aId;
  });

const readImagePreview = (file) =>
  new Promise((resolve, reject) => {
    if (!file) {
      resolve(null);
      return;
    }

    if (!file.type.startsWith("image/")) {
      reject(new Error("Il file selezionato non è un'immagine."));
      return;
    }

    if (file.size > 8 * 1024 * 1024) {
      reject(new Error("Immagine troppo grande. Massimo 8 MB."));
      return;
    }

    const reader = new FileReader();

    reader.onerror = () => reject(new Error("Impossibile leggere l'immagine."));
    reader.onload = () =>
      resolve({
        file,
        dataUrl: reader.result,
        name: file.name || "immagine",
        type: file.type,
      });

    reader.readAsDataURL(file);
  });

const apiFetch = async (label, url, options = {}) => {
  const method = options.method || "GET";

  console.groupCollapsed(
    `%c[TICKET API] ${label}`,
    "color:#ebb51b;font-weight:bold",
  );
  console.log("Metodo:", method);
  console.log("URL:", url);

  if (options.body instanceof FormData) {
    const debugFormData = {};
    options.body.forEach((value, key) => {
      debugFormData[key] =
        value instanceof File
          ? `[FILE] ${value.name} · ${value.type} · ${value.size} bytes`
          : value;
    });
    console.log("Payload FormData:", debugFormData);
  } else if (options.body) {
    try {
      console.log("Payload:", JSON.parse(options.body));
    } catch {
      console.log("Payload raw:", options.body);
    }
  }

  const startedAt = performance.now();

  try {
    console.log("Fetch options:", options);

    const response = await fetch(url, options);
    const text = await response.text();

    console.log("HTTP status:", response.status, response.statusText);
    console.log("Response URL:", response.url);
    console.log("Content-Type:", response.headers.get("content-type"));

    // DEBUG VISIBILE: non è dentro un gruppo collassato.
    console.log("===============================================");
    console.log("[TICKET RAW RESPONSE]");
    console.log("HTTP:", response.status, response.statusText);
    console.log("Content-Type:", response.headers.get("content-type"));
    console.log("Lunghezza body:", text.length);
    console.log("BODY RAW:", JSON.stringify(text));
    console.log("BODY TESTO:", text || "[RISPOSTA VUOTA]");
    console.log("===============================================");

    let data;
    try {
      data = text ? JSON.parse(text) : {};
    } catch (parseError) {
      console.error("[TICKET JSON PARSE ERROR]", parseError);
      console.error("[TICKET JSON NON VALIDO] BODY:", JSON.stringify(text));

      // Se PHP/wrapper stampa rumore prima/dopo il JSON, proviamo comunque
      // a individuare il blocco JSON per capire cosa sta succedendo.
      const firstBrace = text.indexOf("{");
      const lastBrace = text.lastIndexOf("}");

      if (firstBrace !== -1 && lastBrace > firstBrace) {
        const candidate = text.slice(firstBrace, lastBrace + 1);

        console.warn(
          "[TICKET] Trovato possibile JSON dentro risposta sporca:",
          candidate,
        );

        try {
          data = JSON.parse(candidate);
          console.warn(
            "[TICKET] JSON recuperato dalla risposta sporca. Il PHP sta stampando caratteri extra.",
          );
        } catch (fallbackError) {
          const preview = text ? text.slice(0, 1000) : "[RISPOSTA VUOTA]";

          throw new Error(
            `Risposta server non valida (HTTP ${response.status}). RAW: ${preview}`,
          );
        }
      } else {
        const preview = text ? text.slice(0, 1000) : "[RISPOSTA VUOTA]";

        throw new Error(
          `Risposta server non valida (HTTP ${response.status}). RAW: ${preview}`,
        );
      }
    }

    console.log("JSON parsed:", data);
    console.log("Durata:", `${Math.round(performance.now() - startedAt)} ms`);

    if (!response.ok || data?.success === false) {
      const message = data?.details
        ? `${data.error || "Errore API"}: ${data.details}`
        : data?.error || `Errore HTTP ${response.status}`;
      throw new Error(message);
    }

    return data;
  } catch (error) {
    console.error("ERRORE:", error);
    throw error;
  } finally {
    console.groupEnd();
  }
};

const StatusIcon = ({ status, size = 15 }) => {
  if (status === STATUS.CLOSED) {
    return <CheckCircle2 size={size} />;
  }

  if (status === STATUS.WORKING) {
    return <LoaderCircle size={size} />;
  }

  return <CircleDot size={size} />;
};

const EmptyState = ({ title, text }) => (
  <div className="tk-empty">
    <Ticket size={29} />
    <strong>{title}</strong>
    <span>{text}</span>
  </div>
);

const TicketCard = ({
  ticket,
  currentUser,
  currentDepartment,
  isDirector,
  showCreator = true,
  allowStatusChange = false,
  allowReply = true,
  onStatusChange,
  onReply,
}) => {
  const [imageOpen, setImageOpen] = useState(false);
  const [replyImageOpen, setReplyImageOpen] = useState(false);
  const [savingImage, setSavingImage] = useState(false);
  const [savingReplyImage, setSavingReplyImage] = useState(false);
  const [replyOpen, setReplyOpen] = useState(false);
  const [replyText, setReplyText] = useState(ticket.replyText || "");
  const [replyImage, setReplyImage] = useState(null);
  const [replyBusy, setReplyBusy] = useState(false);
  const replyFileInputRef = useRef(null);

  useEffect(() => {
    setReplyText(ticket.replyText || "");
  }, [ticket.replyText]);

  useEffect(() => {
    if ((!imageOpen && !replyImageOpen) || typeof document === "undefined")
      return undefined;

    const body = document.body;
    const html = document.documentElement;

    const previousBodyOverflow = body.style.overflow;
    const previousBodyOverscroll = body.style.overscrollBehavior;
    const previousHtmlOverflow = html.style.overflow;
    const previousHtmlOverscroll = html.style.overscrollBehavior;

    body.style.overflow = "hidden";
    body.style.overscrollBehavior = "none";
    html.style.overflow = "hidden";
    html.style.overscrollBehavior = "none";

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        setImageOpen(false);
        setReplyImageOpen(false);
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);

      body.style.overflow = previousBodyOverflow;
      body.style.overscrollBehavior = previousBodyOverscroll;
      html.style.overflow = previousHtmlOverflow;
      html.style.overscrollBehavior = previousHtmlOverscroll;
    };
  }, [imageOpen, replyImageOpen]);

  const saveAttachment = async () => {
    const imageUrl = ticket.image?.dataUrl;
    if (!imageUrl || savingImage) return;

    setSavingImage(true);

    const safeTicketCode = String(
      ticket.code || `ticket-${ticket.id || "allegato"}`,
    )
      .replace(/[^a-zA-Z0-9_-]+/g, "_")
      .replace(/^_+|_+$/g, "");

    try {
      const response = await fetch(imageUrl, { cache: "no-store" });

      if (!response.ok) {
        throw new Error(`Download allegato fallito: HTTP ${response.status}`);
      }

      const blob = await response.blob();
      const contentType = (blob.type || "").toLowerCase();

      let extension = "jpg";

      if (contentType.includes("png")) extension = "png";
      else if (contentType.includes("webp")) extension = "webp";
      else if (contentType.includes("jpeg") || contentType.includes("jpg"))
        extension = "jpg";
      else {
        try {
          const pathname = new URL(imageUrl, window.location.href).pathname;
          const match = pathname.match(/\.(png|jpe?g|webp)$/i);
          if (match) extension = match[1].toLowerCase().replace("jpeg", "jpg");
        } catch {
          // Manteniamo jpg come fallback.
        }
      }

      const objectUrl = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = objectUrl;
      link.download = `${safeTicketCode || "ticket"}-allegato.${extension}`;
      link.style.display = "none";

      document.body.appendChild(link);
      link.click();
      link.remove();

      window.setTimeout(() => URL.revokeObjectURL(objectUrl), 1500);
    } catch (downloadError) {
      console.error(
        "[TICKET] Impossibile scaricare allegato via fetch:",
        downloadError,
      );

      // Fallback utile se il server impedisce il fetch del blob.
      const link = document.createElement("a");
      link.href = imageUrl;
      link.download = `${safeTicketCode || "ticket"}-allegato`;
      link.target = "_blank";
      link.rel = "noopener noreferrer";
      link.style.display = "none";

      document.body.appendChild(link);
      link.click();
      link.remove();
    } finally {
      setSavingImage(false);
    }
  };

  const saveReplyAttachment = async () => {
    const imageUrl = ticket.replyImage?.dataUrl;
    if (!imageUrl || savingReplyImage) return;

    setSavingReplyImage(true);

    const safeTicketCode = String(
      ticket.code || `ticket-${ticket.id || "risposta"}`,
    )
      .replace(/[^a-zA-Z0-9_-]+/g, "_")
      .replace(/^_+|_+$/g, "");

    try {
      const response = await fetch(imageUrl, { cache: "no-store" });
      if (!response.ok) {
        throw new Error(
          `Download allegato risposta fallito: HTTP ${response.status}`,
        );
      }

      const blob = await response.blob();
      const contentType = (blob.type || "").toLowerCase();
      let extension = "jpg";

      if (contentType.includes("png")) extension = "png";
      else if (contentType.includes("webp")) extension = "webp";
      else if (contentType.includes("jpeg") || contentType.includes("jpg"))
        extension = "jpg";

      const objectUrl = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = objectUrl;
      link.download = `${safeTicketCode || "ticket"}-risposta.${extension}`;
      link.style.display = "none";
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.setTimeout(() => URL.revokeObjectURL(objectUrl), 1500);
    } catch (downloadError) {
      console.error(
        "[TICKET] Impossibile scaricare allegato risposta:",
        downloadError,
      );
      const link = document.createElement("a");
      link.href = imageUrl;
      link.target = "_blank";
      link.rel = "noopener noreferrer";
      link.style.display = "none";
      document.body.appendChild(link);
      link.click();
      link.remove();
    } finally {
      setSavingReplyImage(false);
    }
  };

  const handleReplyImageChange = async (event) => {
    const file = event.target.files?.[0];

    if (!file) {
      setReplyImage(null);
      return;
    }

    try {
      const image = await readImagePreview(file);
      setReplyImage(image);
    } catch (imageError) {
      console.error("[TICKET] Errore allegato risposta:", imageError);
      setReplyImage(null);
      if (replyFileInputRef.current) replyFileInputRef.current.value = "";
    }
  };

  const handleReplySubmit = async () => {
    const text = replyText.trim();
    if (!text || replyBusy || typeof onReply !== "function") return;

    setReplyBusy(true);
    try {
      const result = await onReply(ticket.id, text, replyImage?.file || null);
      if (result?.ok) {
        setReplyOpen(false);
        setReplyImage(null);
        if (replyFileInputRef.current) replyFileInputRef.current.value = "";
      }
    } finally {
      setReplyBusy(false);
    }
  };

  const isDepartmentTicket = !ticket.assignedTo;
  const canManageDepartmentTicket =
    isDepartmentTicket && ticket.targetDepartment === currentDepartment;

  const canManageTicket =
    !isDirector &&
    (ticket.assignedTo === currentUser || canManageDepartmentTicket);

  const canChangeStatus = allowStatusChange && canManageTicket;
  const canReply = allowReply && canManageTicket;


  return (
    <>
      <article className={`tk-ticket-card ${ticket.urgent ? "is-urgent" : ""}`}>
        <div className="tk-ticket-topline">
          <div className="tk-ticket-id">
            <Ticket size={14} />
            {ticket.code || ticket.id}
          </div>

          <div className="tk-badges">
            {ticket.urgent && (
              <span className="tk-badge tk-badge-urgent">
                <AlertTriangle size={12} />
                Urgente
              </span>
            )}

            <span className={`tk-badge tk-status tk-status-${ticket.status}`}>
              <StatusIcon status={ticket.status} size={12} />
              {STATUS_LABELS[ticket.status] || ticket.status}
            </span>
          </div>
        </div>

        <h3>{ticket.title}</h3>

        <p className="tk-ticket-description">{ticket.description}</p>

        {ticket.image?.dataUrl && (
          <button
            type="button"
            className="tk-image-preview"
            onClick={() => setImageOpen(true)}
            aria-label={`Apri immagine allegata a ${ticket.title}`}
          >
            <img
              src={ticket.image.dataUrl}
              alt={ticket.image.name || "Allegato"}
            />
            <span>
              <ImageIcon size={13} />
              Apri allegato
            </span>
          </button>
        )}

        <div className="tk-ticket-meta">
          {showCreator && (
            <div>
              <User size={13} />
              <span>
                Da <b>{displayUser(ticket.createdBy)}</b>
              </span>
            </div>
          )}

          <div>
            <Building2 size={13} />
            <span>{ticket.targetDepartment}</span>
          </div>

          <div>
            <Users size={13} />
            <span>
              A{" "}
              <b>
                {ticket.assignedTo
                  ? displayUser(ticket.assignedTo)
                  : `Tutto il reparto ${ticket.targetDepartment}`}
              </b>
            </span>
          </div>

          <div>
            <Clock3 size={13} />
            <span>{formatDateTime(ticket.createdAt)}</span>
          </div>
        </div>

        {ticket.replyText && (
          <div className="tk-reply-box">
            <div className="tk-reply-heading">
              <MessageSquare size={16} />
              <strong>Risposta</strong>
            </div>

            <p>{ticket.replyText}</p>

            <div className="tk-reply-meta">
              {ticket.replyBy ? displayUser(ticket.replyBy) : "—"}
              {ticket.replyAt ? ` · ${formatDateTime(ticket.replyAt)}` : ""}
            </div>

            {ticket.replyImage?.dataUrl && (
              <button
                type="button"
                className="tk-reply-attachment-button"
                onClick={() => setReplyImageOpen(true)}
              >
                <ImageIcon size={15} />
                Apri allegato risposta
              </button>
            )}
          </div>
        )}

        {canReply && (
          <div className="tk-reply-editor">
            <button
              type="button"
              className="tk-reply-toggle"
              onClick={() => setReplyOpen((value) => !value)}
            >
              <MessageSquare size={15} />
              {ticket.replyText ? "Modifica risposta" : "Rispondi al ticket"}
            </button>

            {replyOpen && (
              <div className="tk-reply-form">
                <textarea
                  rows={4}
                  maxLength={4000}
                  value={replyText}
                  onChange={(event) => setReplyText(event.target.value)}
                  placeholder="Scrivi la risposta al ticket..."
                />

                <div className="tk-reply-actions-row">
                  <label className="tk-reply-file">
                    <input
                      ref={replyFileInputRef}
                      type="file"
                      accept="image/png,image/jpeg,image/webp"
                      onChange={handleReplyImageChange}
                    />
                    <Paperclip size={15} />
                    <span>
                      {replyImage ? replyImage.name : "Allega immagine"}
                    </span>
                  </label>

                  <button
                    type="button"
                    className="tk-reply-save"
                    onClick={handleReplySubmit}
                    disabled={replyBusy || !replyText.trim()}
                  >
                    <Send size={15} />
                    {replyBusy ? "Salvataggio..." : "Salva risposta"}
                  </button>
                </div>

                {replyImage?.dataUrl && (
                  <div className="tk-reply-local-preview">
                    <img
                      src={replyImage.dataUrl}
                      alt="Anteprima allegato risposta"
                    />
                    <button
                      type="button"
                      onClick={() => {
                        setReplyImage(null);
                        if (replyFileInputRef.current)
                          replyFileInputRef.current.value = "";
                      }}
                      aria-label="Rimuovi allegato risposta"
                    >
                      <X size={14} />
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {canChangeStatus && (
          <div className="tk-status-editor">
            <span>Stato ticket</span>

            <div className="tk-status-buttons">
              {Object.values(STATUS).map((status) => (
                <button
                  type="button"
                  key={status}
                  className={
                    ticket.status === status
                      ? "tk-status-choice is-active"
                      : "tk-status-choice"
                  }
                  onClick={() => onStatusChange(ticket.id, status)}
                >
                  <StatusIcon status={status} size={13} />
                  {STATUS_LABELS[status]}
                </button>
              ))}
            </div>
          </div>
        )}

        {ticket.updatedAt && (
          <div className="tk-last-update">
            Ultimo aggiornamento: <b>{formatDateTime(ticket.updatedAt)}</b>
            {ticket.updatedBy ? <> · {displayUser(ticket.updatedBy)}</> : null}
          </div>
        )}
      </article>

      {imageOpen &&
        ticket.image?.dataUrl &&
        typeof document !== "undefined" &&
        createPortal(
          <div
            className="tk-image-modal"
            role="dialog"
            aria-modal="true"
            aria-label="Anteprima allegato"
            onMouseDown={(event) => {
              if (event.target === event.currentTarget) {
                setImageOpen(false);
              }
            }}
          >
            <div className="tk-image-modal-card">
              <button
                type="button"
                className="tk-image-close"
                onClick={() => setImageOpen(false)}
                aria-label="Chiudi anteprima"
              >
                <X size={19} />
              </button>

              <img
                src={ticket.image.dataUrl}
                alt={ticket.image.name || "Allegato ticket"}
              />

              <div className="tk-image-modal-footer">
                <div className="tk-image-caption">
                  {ticket.image.name || "Allegato"}
                </div>

                <button
                  type="button"
                  className="tk-image-download"
                  onClick={saveAttachment}
                  disabled={savingImage}
                >
                  <Download size={15} />
                  {savingImage ? "Salvataggio..." : "Salva immagine"}
                </button>
              </div>
            </div>
          </div>,
          document.body,
        )}

      {replyImageOpen &&
        ticket.replyImage?.dataUrl &&
        typeof document !== "undefined" &&
        createPortal(
          <div
            className="tk-image-modal"
            role="dialog"
            aria-modal="true"
            aria-label="Anteprima allegato risposta"
            onMouseDown={(event) => {
              if (event.target === event.currentTarget) {
                setReplyImageOpen(false);
              }
            }}
          >
            <div className="tk-image-modal-card">
              <button
                type="button"
                className="tk-image-close"
                onClick={() => setReplyImageOpen(false)}
                aria-label="Chiudi anteprima risposta"
              >
                <X size={19} />
              </button>

              <img
                src={ticket.replyImage.dataUrl}
                alt={ticket.replyImage.name || "Allegato risposta"}
              />

              <div className="tk-image-modal-footer">
                <div className="tk-image-caption">
                  {ticket.replyImage.name || "Allegato risposta"}
                </div>

                <button
                  type="button"
                  className="tk-image-download"
                  onClick={saveReplyAttachment}
                  disabled={savingReplyImage}
                >
                  <Download size={15} />
                  {savingReplyImage ? "Salvataggio..." : "Salva immagine"}
                </button>
              </div>
            </div>
          </div>,
          document.body,
        )}
    </>
  );
};

function TicketPage() {
  const { user } = useAuth() || {};

  const currentUser = useMemo(() => normalizeUser(user), [user]);

  useEffect(() => {
    document.title = "Utility Web - Ticket";
  }, []);



const enableNotifications = async () => {
  try {
    setError("");

    if (!currentUser || currentUser === "guest") {
      throw new Error(
        "Devi essere autenticato per attivare le notifiche."
      );
    }

    if (!("Notification" in window)) {
      throw new Error(
        "Questo browser non supporta le notifiche."
      );
    }

    if (!("serviceWorker" in navigator)) {
      throw new Error(
        "Questo browser non supporta i Service Worker."
      );
    }

    if (!("PushManager" in window)) {
      throw new Error(
        "Questo browser non supporta Web Push."
      );
    }

    console.log(
      "[PUSH] Registro Service Worker..."
    );

    const registration =
      await navigator.serviceWorker.register("/sw.js");

    await navigator.serviceWorker.ready;

    console.log(
      "[PUSH] Service Worker pronto:",
      registration
    );

    let permission = Notification.permission;

    if (permission === "default") {
      permission =
        await Notification.requestPermission();
    }

    console.log(
      "[PUSH] Permesso:",
      permission
    );

    if (permission !== "granted") {
      throw new Error(
        "Permesso notifiche non concesso."
      );
    }

    /*
     * Controlliamo se Chrome ha già una subscription.
     */
    let subscription =
      await registration.pushManager.getSubscription();

    if (!subscription) {

      console.log(
        "[PUSH] Creo nuova PushSubscription..."
      );

      subscription =
        await registration.pushManager.subscribe({
          userVisibleOnly: true,

          applicationServerKey:
            urlBase64ToUint8Array(
              VAPID_PUBLIC_KEY
            ),
        });
    }

    console.log(
      "[PUSH] Subscription:",
      subscription
    );

    /*
     * toJSON() ci restituisce:
     *
     * endpoint
     * expirationTime
     * keys.p256dh
     * keys.auth
     */
    const subscriptionJSON =
      subscription.toJSON();

    console.log(
      "[PUSH] Subscription JSON:",
      subscriptionJSON
    );

    console.log(
      "[PUSH] Associo browser all'utente:",
      currentUser
    );

    const response = await fetch(
      PUSH_SUBSCRIBE_URL,
      {
        method: "POST",

        headers: {
          "Content-Type": "application/json",
        },

        body: JSON.stringify({
          utente: currentUser,
          subscription: subscriptionJSON,
        }),
      }
    );

    const text = await response.text();

    console.log(
      "[PUSH] Risposta RAW server:",
      text
    );

    let data;

    try {
      data = JSON.parse(text);
    } catch {
      throw new Error(
        "Il server Push ha restituito una risposta non valida: " +
          text
      );
    }

    if (!response.ok || data.success === false) {
      throw new Error(
        data.error ||
          `Errore HTTP ${response.status}`
      );
    }

    console.log(
      "[PUSH] Browser registrato:",
      data
    );

    setNotice(
      `Notifiche attivate per ${displayUser(
        currentUser
      )}.`
    );

  } catch (pushError) {

    console.error(
      "[PUSH] ERRORE:",
      pushError
    );

    setError(
      pushError?.message ||
        "Errore durante l'attivazione delle notifiche."
    );
  }
};
  const [departments, setDepartments] = useState(FALLBACK_DEPARTMENTS);
  const [serverDepartment, setServerDepartment] = useState(null);
  const [serverViewAll, setServerViewAll] = useState(null);

  const currentDepartment = useMemo(
    () => serverDepartment || findDepartment(currentUser, departments),
    [currentUser, departments, serverDepartment],
  );

  const isDirector =
    serverViewAll !== null ? serverViewAll : GLOBAL_VIEWERS.has(currentUser);

  const [activeTab, setActiveTab] = useState("inbox");
  const [tickets, setTickets] = useState([]);
  const [apiLoading, setApiLoading] = useState(false);
  const [notice, setNotice] = useState("");
  const [error, setError] = useState("");

  const [form, setForm] = useState({
    targetDepartment: "",
    assignedTo: "",
    title: "",
    description: "",
    urgent: false,
    image: null,
  });

  const [imageBusy, setImageBusy] = useState(false);
  const fileInputRef = useRef(null);

  const loadUsersConfig = async () => {
    if (!currentUser || currentUser === "guest") return;

    console.log("[TICKET] Carico configurazione reparti per:", currentUser);

    try {
      const data = await apiFetch(
        "GET configurazione utenti/reparti",
        `${API_URL}?action=users&utente=${encodeURIComponent(currentUser)}`,
      );

      if (data.reparti && typeof data.reparti === "object") {
        const normalizedDepartments = Object.fromEntries(
          Object.entries(data.reparti).map(([department, members]) => [
            department,
            Array.isArray(members)
              ? members.map((member) => normalizeUser(member))
              : [],
          ]),
        );

        setDepartments(normalizedDepartments);
      }

      setServerDepartment(data.reparto || null);
      setServerViewAll(Boolean(data.vede_tutti));

      console.log("[TICKET] Reparto corrente:", data.reparto);
      console.log("[TICKET] Visione completa:", Boolean(data.vede_tutti));
    } catch (requestError) {
      console.error(
        "[TICKET] Errore config utenti, uso fallback locale:",
        requestError,
      );
      setDepartments(FALLBACK_DEPARTMENTS);
      setServerDepartment(null);
      setServerViewAll(null);
    }
  };

  const loadTickets = async ({ silent = false } = {}) => {
    if (!currentUser || currentUser === "guest") return;

    if (!silent) setApiLoading(true);
    setError("");

    console.log("[TICKET] Carico ticket per:", currentUser);

    try {
      const data = await apiFetch(
        "GET lista ticket",
        `${API_URL}?utente=${encodeURIComponent(currentUser)}`,
      );

      const normalized = Array.isArray(data.tickets)
        ? data.tickets.map(normalizeTicket).filter(Boolean)
        : [];

      console.log("[TICKET] Ticket raw ricevuti:", data.tickets || []);
      console.log("[TICKET] Ticket normalizzati:", normalized);

      setTickets(normalized);

      if (data.reparto !== undefined) {
        setServerDepartment(data.reparto || null);
      }

      if (data.vede_tutti !== undefined) {
        setServerViewAll(Boolean(data.vede_tutti));
      }
    } catch (requestError) {
      console.error("[TICKET] GET ticket fallita:", requestError);
      setError(requestError.message || "Errore caricamento ticket.");
    } finally {
      if (!silent) setApiLoading(false);
    }
  };

  useEffect(() => {
    if (!user || currentUser === "guest") return;

    console.log("===============================================");
    console.log("[TICKET] Avvio pagina Ticket");
    console.log("[TICKET] Auth user:", user);
    console.log("[TICKET] Username normalizzato:", currentUser);
    console.log("[TICKET] API_URL:", API_URL);
    console.log("===============================================");

    loadUsersConfig();
    loadTickets();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser, user]);

  // Aggiornamento automatico: riceve i nuovi ticket senza premere F5.
  useEffect(() => {
    if (!user || currentUser === "guest") return undefined;

    const intervalId = window.setInterval(() => {
      loadTickets({ silent: true });
    }, 10000);

    return () => window.clearInterval(intervalId);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser, user]);

  useEffect(() => {
    if (!notice) return;

    const timeout = window.setTimeout(() => setNotice(""), 3200);
    return () => window.clearTimeout(timeout);
  }, [notice]);

  const activeTickets = useMemo(
    () => tickets.filter((ticket) => ticket.status !== STATUS.CLOSED),
    [tickets],
  );

  const closedTickets = useMemo(
    () =>
      [...tickets]
        .filter((ticket) => ticket.status === STATUS.CLOSED)
        .sort((a, b) => {
          const aDate =
            parseDbDate(a.resolvedAt || a.updatedAt || a.createdAt)?.getTime() ?? 0;
          const bDate =
            parseDbDate(b.resolvedAt || b.updatedAt || b.createdAt)?.getTime() ?? 0;
          return bDate - aDate;
        }),
    [tickets],
  );

  const assignedToMe = useMemo(
    () =>
      sortTickets(
        activeTickets.filter(
          (ticket) =>
            ticket.assignedTo === currentUser ||
            (!ticket.assignedTo &&
              ticket.targetDepartment === currentDepartment),
        ),
      ),
    [activeTickets, currentUser, currentDepartment],
  );

  const departmentTickets = useMemo(() => {
    const list = isDirector
      ? activeTickets
      : activeTickets.filter(
          (ticket) => ticket.targetDepartment === currentDepartment,
        );

    return sortTickets(list);
  }, [activeTickets, currentDepartment, isDirector]);

  const myCreatedTickets = useMemo(
    () =>
      sortTickets(
        activeTickets.filter((ticket) => ticket.createdBy === currentUser),
      ),
    [activeTickets, currentUser],
  );

  const visibleClosedTickets = useMemo(() => {
    if (isDirector) return closedTickets;

    return closedTickets.filter(
      (ticket) =>
        ticket.createdBy === currentUser ||
        ticket.assignedTo === currentUser ||
        ticket.targetDepartment === currentDepartment,
    );
  }, [closedTickets, currentUser, currentDepartment, isDirector]);

  const myOpenCount = myCreatedTickets.length;
  const receivedOpenCount = assignedToMe.length;

  const departmentMembers = form.targetDepartment
    ? departments[form.targetDepartment] || []
    : [];

  const resetForm = () => {
    setForm({
      targetDepartment: "",
      assignedTo: "",
      title: "",
      description: "",
      urgent: false,
      image: null,
    });

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleDepartmentChange = (event) => {
    const targetDepartment = event.target.value;

    console.log("[TICKET] Reparto selezionato:", targetDepartment);

    setForm((prev) => ({
      ...prev,
      targetDepartment,
      assignedTo: "",
    }));
  };

  const handleImageChange = async (event) => {
    const file = event.target.files?.[0];

    setError("");

    if (!file) {
      setForm((prev) => ({ ...prev, image: null }));
      return;
    }

    console.log("[TICKET] Allegato selezionato:", {
      name: file.name,
      type: file.type,
      size: file.size,
    });

    try {
      setImageBusy(true);
      const image = await readImagePreview(file);
      setForm((prev) => ({ ...prev, image }));
    } catch (imageError) {
      console.error("[TICKET] Errore allegato:", imageError);
      setForm((prev) => ({ ...prev, image: null }));
      setError(imageError.message || "Errore caricamento immagine.");

      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    } finally {
      setImageBusy(false);
    }
  };

  const submitTicket = async (event) => {
    event.preventDefault();
    setError("");

    if (!form.targetDepartment) {
      setError("Seleziona il reparto destinatario.");
      return;
    }

    const title = form.title.trim();
    const description = form.description.trim();

    if (!title) {
      setError("Inserisci un titolo per il ticket.");
      return;
    }

    if (!description) {
      setError("Inserisci il testo del ticket.");
      return;
    }

    const payload = new FormData();
    payload.append("utente_creazione", currentUser);
    payload.append("reparto_destinatario", form.targetDepartment);
    payload.append("utente_destinatario", form.assignedTo ? normalizeUser(form.assignedTo) : "");
    payload.append("titolo", title);
    payload.append("descrizione", description);
    payload.append("urgente", form.urgent ? "1" : "0");

    if (form.image?.file) {
      payload.append("allegato", form.image.file);
    }

    console.log("[TICKET] Invio nuovo ticket:", {
      utente_creazione: currentUser,
      reparto_creazione: currentDepartment,
      reparto_destinatario: form.targetDepartment,
      utente_destinatario: form.assignedTo ? normalizeUser(form.assignedTo) : "",
      titolo: title,
      descrizione: description,
      urgente: form.urgent,
      allegato: form.image?.file
        ? `${form.image.file.name} (${form.image.file.size} bytes)`
        : null,
    });

    try {
      setApiLoading(true);

      const data = await apiFetch("POST crea ticket", API_URL, {
        method: "POST",
        body: payload,
      });

      const createdTicket = normalizeTicket(data.ticket);
      console.log("[TICKET] POST SUCCESS");
      console.log("[TICKET] Ticket creato:", createdTicket || data.ticket);
      console.log("[TICKET] Ricarico lista ticket dopo POST...");

      resetForm();
      setNotice(
        `Ticket ${createdTicket?.code || data.ticket?.codice_ticket || ""} creato correttamente.`,
      );
      setActiveTab("mine");

      await loadTickets({ silent: true });
    } catch (requestError) {
      console.error("[TICKET] Creazione fallita:", requestError);
      setError(
        requestError.message || "Errore durante la creazione del ticket.",
      );
    } finally {
      setApiLoading(false);
    }
  };

  const submitTicketReply = async (ticketId, replyText, replyFile) => {
    setError("");

    const payload = new FormData();
    payload.append("action", "reply");
    payload.append("id_ticket", String(ticketId));
    payload.append("utente", currentUser);
    payload.append("risposta", replyText);

    if (replyFile) {
      payload.append("allegato_risposta", replyFile);
    }

    try {
      setApiLoading(true);

      const data = await apiFetch("POST risposta ticket", API_URL, {
        method: "POST",
        body: payload,
      });

      setNotice("Risposta salvata correttamente.");
      await loadTickets({ silent: true });

      return { ok: true, ticket: data.ticket };
    } catch (requestError) {
      console.error("[TICKET] Risposta ticket fallita:", requestError);
      setError(
        requestError.message || "Errore durante il salvataggio della risposta.",
      );
      return { ok: false };
    } finally {
      setApiLoading(false);
    }
  };

  const updateTicketStatus = async (ticketId, nextStatus) => {
    if (!Object.values(STATUS).includes(nextStatus)) return;

    const currentTicket = tickets.find(
      (ticket) => String(ticket.id) === String(ticketId),
    );

    console.log("[TICKET] Cambio stato richiesto:", {
      id_ticket: ticketId,
      codice_ticket: currentTicket?.code,
      da: currentTicket?.status,
      a: nextStatus,
      utente: currentUser,
    });

    try {
      setApiLoading(true);
      setError("");

      const data = await apiFetch("PUT cambia stato", API_URL, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          id_ticket: ticketId,
          utente: currentUser,
          stato: nextStatus,
        }),
      });

      console.log("[TICKET] PUT completata:", data);
      setNotice(`Stato aggiornato: ${STATUS_LABELS[nextStatus]}.`);

      await loadTickets({ silent: true });
    } catch (requestError) {
      console.error("[TICKET] Cambio stato fallito:", requestError);
      setError(requestError.message || "Errore aggiornamento stato.");
    } finally {
      setApiLoading(false);
    }
  };

  const tabs = [
    {
      id: "inbox",
      label: "Ticket ricevuti",
      icon: Inbox,
      count: receivedOpenCount,
    },
    {
      id: "new",
      label: "Nuovo ticket",
      icon: Plus,
    },
    {
      id: "mine",
      label: "Stato dei miei ticket",
      icon: Ticket,
      count: myOpenCount,
    },
    {
      id: "closed",
      label: "Ticket chiusi",
      icon: CheckCircle2,
      count: visibleClosedTickets.length,
    },
  ];

  return (
    <div
      className="tk-shell w-100"
      style={{
        overflowY: "scroll",
        scrollbarWidth: "none",
        msOverflowStyle: "none",
      }}
    >
      <NavbarUtility />
      <div className="tk-ambient tk-ambient-one" />
      <div className="tk-ambient tk-ambient-two" />

      <div className="tk-page">
        <header className="tk-hero">
          <div>
            <div className="tk-eyebrow">
              <span />
              Assistenza aziendale
            </div>

            <h1>
              Centro <strong>Ticket</strong>
            </h1>

            <p>
              Invia richieste a un intero reparto oppure assegnale a una persona e
              segui lo stato della lavorazione.
            </p>
          </div>

          <div className="tk-user-glass">
            <button
              type="button"
              onClick={enableNotifications}
              style={{
                border: "1px solid rgba(235,181,27,.35)",
                background: "rgba(235,181,27,.12)",
                color: "#ebb51b",
                borderRadius: "10px",
                padding: "8px 12px",
                cursor: "pointer",
                fontWeight: 700,
              }}
            >
              🔔 Attiva notifiche
            </button>
            <div className="tk-user-avatar">
              {displayUser(currentUser).charAt(0) || "U"}
            </div>

            <div>
              <span>Accesso come</span>
              <strong>{displayUser(currentUser)}</strong>
              <small>
                {isDirector
                  ? "Direzione · visione completa"
                  : currentDepartment}
              </small>
            </div>
          </div>
        </header>

        {currentDepartment === "NON ASSEGNATO" && !isDirector && (
          <div className="tk-warning-banner">
            <AlertTriangle size={17} />
            <span>
              L'utente <b>{displayUser(currentUser)}</b> non è presente nella
              mappa reparti di Ticket.jsx. Può creare ticket, ma la vista
              reparto resterà vuota finché non gli assegni un reparto.
            </span>
          </div>
        )}

        <nav className="tk-tabs" aria-label="Sezioni ticket">
          {tabs.map((tab) => {
            const Icon = tab.icon;

            return (
              <button
                type="button"
                key={tab.id}
                className={activeTab === tab.id ? "tk-tab is-active" : "tk-tab"}
                onClick={() => {
                  setActiveTab(tab.id);
                  setError("");
                }}
              >
                <Icon size={17} />
                <span>{tab.label}</span>

                {typeof tab.count === "number" && tab.count > 0 && (
                  <b>{tab.count}</b>
                )}
              </button>
            );
          })}
        </nav>

        {notice && (
          <div className="tk-notice" role="status">
            <CheckCircle2 size={17} />
            {notice}
          </div>
        )}

        {error && (
          <div className="tk-error" role="alert">
            <AlertTriangle size={17} />
            {error}
          </div>
        )}

        {apiLoading && (
          <div className="tk-notice" role="status">
            <LoaderCircle size={17} />
            Sto inviando il ticket, attendere prego...
          </div>
        )}

        {activeTab === "inbox" && (
          <div className="tk-tab-panel">
            <section className="tk-section">
              <div className="tk-section-heading">
                <div>
                  <span className="tk-section-kicker">Personali</span>
                  <h2>I miei ticket ricevuti</h2>
                  <p>Richieste assegnate direttamente a te.</p>
                </div>

                <div className="tk-counter">{assignedToMe.length}</div>
              </div>

              {assignedToMe.length > 0 ? (
                <div className="tk-ticket-grid">
                  {assignedToMe.map((ticket) => (
                    <TicketCard
                      key={ticket.id}
                      ticket={ticket}
                      currentUser={currentUser}
                      currentDepartment={currentDepartment}
                      isDirector={isDirector}
                      allowStatusChange
                      onStatusChange={updateTicketStatus}
                      onReply={submitTicketReply}
                    />
                  ))}
                </div>
              ) : (
                <EmptyState
                  title="Nessun ticket assegnato"
                  text="Quando qualcuno assegnerà una richiesta direttamente a te, apparirà qui."
                />
              )}
            </section>

            <section className="tk-section">
              <div className="tk-section-heading">
                <div>
                  <span className="tk-section-kicker">
                    {isDirector ? "Tutti i reparti" : "Reparto"}
                  </span>

                  <h2>
                    {isDirector
                      ? "Ticket aziendali"
                      : `Ticket ${currentDepartment}`}
                  </h2>

                  <p>
                    {isDirector
                      ? "Alessandro e Rita possono consultare tutte le richieste aziendali."
                      : "Tutte le richieste destinate al tuo reparto, comprese quelle assegnate a te."}
                  </p>
                </div>

                <div className="tk-counter">{departmentTickets.length}</div>
              </div>

              {departmentTickets.length > 0 ? (
                <div className="tk-ticket-grid">
                  {departmentTickets.map((ticket) => (
                    <TicketCard
                      key={ticket.id}
                      ticket={ticket}
                      currentUser={currentUser}
                      currentDepartment={currentDepartment}
                      isDirector={isDirector}
                      allowStatusChange={!isDirector}
                      allowReply={!isDirector}
                      onStatusChange={updateTicketStatus}
                      onReply={submitTicketReply}
                    />
                  ))}
                </div>
              ) : (
                <EmptyState
                  title="Nessun ticket di reparto"
                  text={
                    isDirector
                      ? "Non risultano richieste aziendali."
                      : "Non risultano richieste destinate al tuo reparto."
                  }
                />
              )}
            </section>
          </div>
        )}

        {activeTab === "new" && (
          <div className="tk-tab-panel">
            <section className="tk-compose-layout">
              <form className="tk-compose-card" onSubmit={submitTicket}>
                <div className="tk-compose-header">
                  <div className="tk-compose-icon">
                    <Plus size={24} />
                  </div>

                  <div>
                    <span>Nuova richiesta</span>
                    <h2>Scrivi un ticket</h2>
                    <p>
                      Scegli il reparto. L'assegnazione a una persona è facoltativa.
                    </p>
                  </div>
                </div>

                <div className="tk-form-grid">
                  <label className="tk-field">
                    <span>
                      <Building2 size={14} />
                      Reparto destinatario
                    </span>

                    <select
                      value={form.targetDepartment}
                      onChange={handleDepartmentChange}
                      required
                    >
                      <option value="">Seleziona reparto</option>

                      {Object.keys(departments).map((department) => (
                        <option key={department} value={department}>
                          {department}
                        </option>
                      ))}
                    </select>
                  </label>

                  <label className="tk-field">
                    <span>
                      <User size={14} />
                      Assegna a
                    </span>

                    <select
                      value={form.assignedTo}
                      onChange={(event) =>
                        setForm((prev) => ({
                          ...prev,
                          assignedTo: event.target.value,
                        }))
                      }
                      disabled={!form.targetDepartment}
                    >
                      <option value="">
                        {form.targetDepartment
                          ? `Tutto il reparto ${form.targetDepartment}`
                          : "Prima scegli il reparto"}
                      </option>

                      {departmentMembers.map((member) => (
                        <option key={member} value={member}>
                          {displayUser(member)}
                        </option>
                      ))}
                    </select>
                  </label>
                </div>

                <label className="tk-field">
                  <span>
                    <Ticket size={14} />
                    Titolo
                  </span>

                  <input
                    type="text"
                    maxLength={120}
                    placeholder="Es. Problema accesso CRM"
                    value={form.title}
                    onChange={(event) =>
                      setForm((prev) => ({
                        ...prev,
                        title: event.target.value,
                      }))
                    }
                    required
                  />
                </label>

                <label className="tk-field">
                  <span>
                    <Send size={14} />
                    Descrizione
                  </span>

                  <textarea
                    rows={7}
                    maxLength={4000}
                    placeholder="Descrivi il problema o la richiesta nel modo più chiaro possibile..."
                    value={form.description}
                    onChange={(event) =>
                      setForm((prev) => ({
                        ...prev,
                        description: event.target.value,
                      }))
                    }
                    required
                  />
                  <small>{form.description.length}/4000</small>
                </label>

                <div className="tk-compose-options">
                  <label className="tk-urgent-toggle">
                    <input
                      type="checkbox"
                      checked={form.urgent}
                      onChange={(event) =>
                        setForm((prev) => ({
                          ...prev,
                          urgent: event.target.checked,
                        }))
                      }
                    />

                    <span className="tk-switch" />

                    <div>
                      <b>
                        <AlertTriangle size={14} />
                        Ticket urgente
                      </b>
                      <small>
                        Verrà evidenziato e mostrato prima degli altri.
                      </small>
                    </div>
                  </label>

                  <label className="tk-attachment">
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/png,image/jpeg,image/webp"
                      onChange={handleImageChange}
                    />

                    <Paperclip size={16} />

                    <div>
                      <b>
                        {imageBusy
                          ? "Elaborazione..."
                          : form.image
                            ? form.image.name
                            : "Allega immagine"}
                      </b>
                      <small>PNG, JPG o WEBP · 1 immagine</small>
                    </div>
                  </label>
                </div>

                {form.image?.dataUrl && (
                  <div className="tk-compose-image">
                    <img src={form.image.dataUrl} alt="Anteprima allegato" />

                    <button
                      type="button"
                      onClick={() => {
                        setForm((prev) => ({ ...prev, image: null }));

                        if (fileInputRef.current) {
                          fileInputRef.current.value = "";
                        }
                      }}
                      aria-label="Rimuovi immagine"
                    >
                      <X size={16} />
                    </button>
                  </div>
                )}

                <div className="tk-form-actions">
                  <button
                    type="button"
                    className="tk-btn-secondary"
                    onClick={resetForm}
                  >
                    Pulisci
                  </button>

                  <button
                    type="submit"
                    className="tk-btn-primary"
                    disabled={imageBusy}
                  >
                    <Send size={16} />
                    Invia ticket
                  </button>
                </div>
              </form>

              <aside className="tk-compose-side">
                <span>Riepilogo</span>

                <div className="tk-summary-row">
                  <Building2 size={17} />
                  <div>
                    <small>Reparto</small>
                    <strong>{form.targetDepartment || "Da scegliere"}</strong>
                  </div>
                </div>

                <div className="tk-summary-row">
                  <User size={17} />
                  <div>
                    <small>Destinatario</small>
                    <strong>
                      {form.assignedTo
                        ? displayUser(form.assignedTo)
                        : form.targetDepartment
                          ? `Tutto il reparto ${form.targetDepartment}`
                          : "Da scegliere"}
                    </strong>
                  </div>
                </div>

                <div className="tk-summary-row">
                  <AlertTriangle size={17} />
                  <div>
                    <small>Priorità</small>
                    <strong>{form.urgent ? "Urgente" : "Normale"}</strong>
                  </div>
                </div>

                <div className="tk-summary-row">
                  <ImageIcon size={17} />
                  <div>
                    <small>Allegato</small>
                    <strong>{form.image ? "1 immagine" : "Nessuno"}</strong>
                  </div>
                </div>
              </aside>
            </section>
          </div>
        )}

        {activeTab === "mine" && (
          <div className="tk-tab-panel">
            <section className="tk-section">
              <div className="tk-section-heading">
                <div>
                  <span className="tk-section-kicker">Le mie richieste</span>
                  <h2>Stato dei miei ticket</h2>
                  <p>
                    Qui trovi esclusivamente i ticket che hai aperto tu e il
                    relativo stato.
                  </p>
                </div>

                <div className="tk-stats-inline">
                  <span>
                    <CircleDot size={13} />
                    {
                      myCreatedTickets.filter(
                        (ticket) => ticket.status === STATUS.OPEN,
                      ).length
                    }{" "}
                    aperti
                  </span>

                  <span>
                    <LoaderCircle size={13} />
                    {
                      myCreatedTickets.filter(
                        (ticket) => ticket.status === STATUS.WORKING,
                      ).length
                    }{" "}
                    in lavorazione
                  </span>
                </div>
              </div>

              {myCreatedTickets.length > 0 ? (
                <div className="tk-ticket-grid">
                  {myCreatedTickets.map((ticket) => (
                    <TicketCard
                      key={ticket.id}
                      ticket={ticket}
                      currentUser={currentUser}
                      currentDepartment={currentDepartment}
                      isDirector={isDirector}
                      showCreator={false}
                      allowStatusChange
                      onStatusChange={updateTicketStatus}
                      onReply={submitTicketReply}
                    />
                  ))}
                </div>
              ) : (
                <EmptyState
                  title="Non hai ancora aperto ticket"
                  text="Vai nella scheda “Nuovo ticket” per creare la tua prima richiesta."
                />
              )}
            </section>
          </div>
        )}

        {activeTab === "closed" && (
          <div className="tk-tab-panel">
            <section className="tk-section">
              <div className="tk-section-heading">
                <div>
                  <span className="tk-section-kicker">Archivio recente</span>
                  <h2>Ticket chiusi</h2>
                  <p>
                    Ticket risolti nell'ultimo mese, ordinati dal più recente.
                  </p>
                </div>
                <div className="tk-counter">{visibleClosedTickets.length}</div>
              </div>

              {visibleClosedTickets.length > 0 ? (
                <div className="tk-closed-table-wrap">
                  <table className="tk-closed-table">
                    <thead>
                      <tr>
                        <th>Ticket</th>
                        <th>Data</th>
                        <th>Titolo</th>
                        <th>Da</th>
                        <th>Reparto</th>
                        <th>Assegnato a</th>
                        <th>Chiuso il</th>
                      </tr>
                    </thead>
                    <tbody>
                      {visibleClosedTickets.map((ticket) => (
                        <tr key={ticket.id}>
                          <td data-label="Ticket">
                            <span className="tk-table-code">{ticket.code}</span>
                            {ticket.urgent && (
                              <span className="tk-table-urgent">
                                <AlertTriangle size={11} />
                                Urgente
                              </span>
                            )}
                          </td>
                          <td data-label="Data">{formatDateTime(ticket.createdAt)}</td>
                          <td data-label="Titolo">
                            <strong>{ticket.title}</strong>
                          </td>
                          <td data-label="Da">{displayUser(ticket.createdBy)}</td>
                          <td data-label="Reparto">{ticket.targetDepartment}</td>
                          <td data-label="Assegnato a">
                            {ticket.assignedTo
                              ? displayUser(ticket.assignedTo)
                              : "Tutto il reparto"}
                          </td>
                          <td data-label="Chiuso il">
                            {formatDateTime(ticket.resolvedAt || ticket.updatedAt)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <EmptyState
                  title="Nessun ticket chiuso"
                  text="I ticket risolti compariranno qui separati dalle richieste attive."
                />
              )}
            </section>
          </div>
        )}
      </div>

      <style>{`
        * {
          box-sizing: border-box;
        }

        .tk-shell {
          position: relative;
          min-height: 100vh;
          overflow: hidden;
          color: #f7f7f8;
          background:
            radial-gradient(
              circle at 8% 8%,
              rgba(235,181,27,0.13),
              transparent 26%
            ),
            radial-gradient(
              circle at 91% 18%,
              rgba(255,255,255,0.07),
              transparent 25%
            ),
            radial-gradient(
              circle at 52% 100%,
              rgba(84,100,130,0.12),
              transparent 36%
            ),
            #202227;
        }

        .tk-shell::before {
          content: "";
          position: absolute;
          inset: 0;
          pointer-events: none;
          background:
            linear-gradient(
              135deg,
              rgba(255,255,255,0.025),
              transparent 35%
            );
        }

        .tk-ambient {
          position: absolute;
          pointer-events: none;
          border-radius: 50%;
          filter: blur(75px);
          opacity: 0.32;
        }

        .tk-ambient-one {
          width: 300px;
          height: 300px;
          top: 22%;
          left: -130px;
          background: rgba(235,181,27,0.12);
        }

        .tk-ambient-two {
          width: 340px;
          height: 340px;
          right: -150px;
          top: 48%;
          background: rgba(110,130,170,0.10);
        }

        .tk-page {
          position: relative;
          z-index: 2;
          width: min(1420px, calc(100% - 40px));
          margin: 0 auto;
          padding: 42px 0 75px;
        }

        .tk-hero {
          display: flex;
          align-items: flex-end;
          justify-content: space-between;
          gap: 28px;
          margin-bottom: 31px;
        }

        .tk-eyebrow {
          display: flex;
          align-items: center;
          gap: 9px;
          margin-bottom: 13px;
          color: rgba(247,247,248,0.48);
          font-size: 10px;
          font-weight: 800;
          letter-spacing: 1.9px;
          text-transform: uppercase;
        }

        .tk-eyebrow span {
          width: 7px;
          height: 7px;
          flex: 0 0 auto;
          border-radius: 50%;
          background: ${GOLD};
          box-shadow: 0 0 12px rgba(235,181,27,0.65);
        }

        .tk-hero h1 {
          margin: 0;
          color: #f7f7f8;
          font-size: clamp(42px, 5vw, 68px);
          line-height: 0.98;
          font-weight: 300;
          letter-spacing: -3px;
        }

        .tk-hero h1 strong {
          color: ${GOLD};
          font-weight: 650;
          text-shadow: 0 0 28px rgba(235,181,27,0.18);
        }

        .tk-hero > div:first-child > p {
          max-width: 650px;
          margin: 20px 0 0;
          color: rgba(247,247,248,0.62);
          font-size: 14px;
          line-height: 1.7;
        }

        .tk-user-glass {
          flex: 0 0 auto;
          display: flex;
          align-items: center;
          gap: 12px;
          min-width: 250px;
          padding: 13px 15px;
          border: 1px solid rgba(255,255,255,0.14);
          border-radius: 21px;
          background:
            linear-gradient(
              145deg,
              rgba(255,255,255,0.105),
              rgba(255,255,255,0.042)
            );
          box-shadow:
            0 18px 45px rgba(0,0,0,0.20),
            inset 0 1px 0 rgba(255,255,255,0.15);
          backdrop-filter: blur(24px) saturate(135%);
          -webkit-backdrop-filter: blur(24px) saturate(135%);
        }

        .tk-user-avatar {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 43px;
          height: 43px;
          flex: 0 0 auto;
          border-radius: 14px;
          color: #202227;
          background: ${GOLD};
          font-size: 15px;
          font-weight: 850;
          box-shadow:
            0 8px 22px rgba(235,181,27,0.13),
            inset 0 1px 0 rgba(255,255,255,0.35);
        }

        .tk-user-glass > div:last-child {
          min-width: 0;
          display: flex;
          flex-direction: column;
        }

        .tk-user-glass span {
          margin-bottom: 1px;
          color: rgba(255,255,255,0.34);
          font-size: 8px;
          font-weight: 800;
          letter-spacing: 1.2px;
          text-transform: uppercase;
        }

        .tk-user-glass strong {
          overflow: hidden;
          color: rgba(255,255,255,0.88);
          font-size: 12px;
          font-weight: 700;
          white-space: nowrap;
          text-overflow: ellipsis;
        }

        .tk-user-glass small {
          margin-top: 2px;
          color: ${GOLD};
          font-size: 9px;
          font-weight: 650;
        }

        .tk-warning-banner,
        .tk-notice,
        .tk-error {
          display: flex;
          align-items: flex-start;
          gap: 9px;
          margin-bottom: 15px;
          padding: 12px 14px;
          border-radius: 15px;
          font-size: 11px;
          line-height: 1.5;
          backdrop-filter: blur(18px);
          -webkit-backdrop-filter: blur(18px);
        }

        .tk-warning-banner,
        .tk-error {
          color: rgba(255,222,222,0.92);
          border: 1px solid rgba(255,110,110,0.22);
          background: rgba(140,35,35,0.16);
        }

        .tk-notice {
          color: rgba(226,255,231,0.92);
          border: 1px solid rgba(120,220,145,0.20);
          background: rgba(40,120,65,0.14);
        }

        .tk-tabs {
          display: flex;
          align-items: center;
          gap: 7px;
          width: fit-content;
          max-width: 100%;
          margin-bottom: 18px;
          padding: 6px;
          overflow-x: auto;
          border: 1px solid rgba(255,255,255,0.13);
          border-radius: 20px;
          background:
            linear-gradient(
              145deg,
              rgba(255,255,255,0.085),
              rgba(255,255,255,0.033)
            );
          box-shadow:
            0 16px 42px rgba(0,0,0,0.17),
            inset 0 1px 0 rgba(255,255,255,0.13);
          backdrop-filter: blur(24px) saturate(135%);
          -webkit-backdrop-filter: blur(24px) saturate(135%);
          scrollbar-width: none;
        }

        .tk-tabs::-webkit-scrollbar {
          display: none;
        }

        .tk-tab {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 7px;
          min-height: 42px;
          padding: 0 14px;
          border: 1px solid transparent;
          border-radius: 14px;
          color: rgba(255,255,255,0.49);
          background: transparent;
          font-size: 10px;
          font-weight: 700;
          white-space: nowrap;
          cursor: pointer;
          transition:
            background 0.2s ease,
            border-color 0.2s ease,
            color 0.2s ease;
        }

        .tk-tab:hover {
          color: rgba(255,255,255,0.82);
          background: rgba(255,255,255,0.05);
        }

        .tk-tab.is-active {
          color: ${GOLD};
          border-color: rgba(235,181,27,0.23);
          background:
            linear-gradient(
              145deg,
              rgba(235,181,27,0.12),
              rgba(235,181,27,0.045)
            );
          box-shadow: inset 0 1px 0 rgba(255,255,255,0.10);
        }

        .tk-tab b {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          min-width: 20px;
          height: 20px;
          padding: 0 6px;
          border-radius: 999px;
          color: #202227;
          background: ${GOLD};
          font-size: 9px;
          font-weight: 900;
        }

        .tk-tab-panel {
          animation: tk-panel-in 0.22s ease both;
        }

        @keyframes tk-panel-in {
          from {
            opacity: 0;
            transform: translateY(4px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .tk-section {
          margin-top: 18px;
          padding: 22px;
          border: 1px solid rgba(255,255,255,0.13);
          border-radius: 27px;
          background:
            linear-gradient(
              145deg,
              rgba(255,255,255,0.083),
              rgba(255,255,255,0.031)
            );
          box-shadow:
            0 24px 65px rgba(0,0,0,0.19),
            inset 0 1px 0 rgba(255,255,255,0.14);
          backdrop-filter: blur(26px) saturate(135%);
          -webkit-backdrop-filter: blur(26px) saturate(135%);
        }

        .tk-section-heading {
          display: flex;
          align-items: flex-start;
          justify-content: space-between;
          gap: 20px;
          margin-bottom: 17px;
        }

        .tk-section-kicker {
          display: block;
          margin-bottom: 3px;
          color: ${GOLD};
          font-size: 8px;
          font-weight: 850;
          letter-spacing: 1.5px;
          text-transform: uppercase;
        }

        .tk-section-heading h2 {
          margin: 0;
          color: #f7f7f8;
          font-size: 20px;
          font-weight: 650;
          letter-spacing: -0.5px;
        }

        .tk-section-heading p {
          margin: 4px 0 0;
          color: rgba(255,255,255,0.40);
          font-size: 10px;
          line-height: 1.5;
        }

        .tk-counter {
          display: flex;
          align-items: center;
          justify-content: center;
          min-width: 39px;
          height: 39px;
          padding: 0 10px;
          border: 1px solid rgba(255,255,255,0.11);
          border-radius: 13px;
          color: rgba(255,255,255,0.64);
          background: rgba(255,255,255,0.055);
          font-size: 11px;
          font-weight: 850;
        }

        .tk-closed-table-wrap {
          width: 100%;
          overflow-x: auto;
          border: 1px solid rgba(255,255,255,0.10);
          border-radius: 20px;
          background:
            linear-gradient(
              145deg,
              rgba(255,255,255,0.065),
              rgba(255,255,255,0.025)
            );
          box-shadow:
            inset 0 1px 0 rgba(255,255,255,0.10),
            0 14px 34px rgba(0,0,0,0.12);
          backdrop-filter: blur(22px) saturate(135%);
          -webkit-backdrop-filter: blur(22px) saturate(135%);
        }

        .tk-closed-table {
          width: 100%;
          min-width: 940px;
          border-collapse: collapse;
        }

        .tk-closed-table th {
          padding: 13px 14px;
          color: rgba(255,255,255,0.34);
          border-bottom: 1px solid rgba(255,255,255,0.09);
          font-size: 8px;
          font-weight: 850;
          letter-spacing: 1px;
          text-align: left;
          text-transform: uppercase;
        }

        .tk-closed-table td {
          padding: 14px;
          color: rgba(255,255,255,0.62);
          border-bottom: 1px solid rgba(255,255,255,0.055);
          font-size: 10px;
          vertical-align: middle;
        }

        .tk-closed-table tbody tr {
          transition: background 0.2s ease;
        }

        .tk-closed-table tbody tr:hover {
          background: rgba(255,255,255,0.045);
        }

        .tk-closed-table tbody tr:last-child td {
          border-bottom: 0;
        }

        .tk-closed-table td strong {
          color: rgba(255,255,255,0.88);
          font-size: 11px;
        }

        .tk-table-code {
          display: block;
          color: ${GOLD};
          font-size: 9px;
          font-weight: 800;
        }

        .tk-table-urgent {
          display: inline-flex;
          align-items: center;
          gap: 4px;
          margin-top: 4px;
          color: ${GOLD};
          font-size: 8px;
          font-weight: 800;
        }

        .tk-ticket-grid {
          display: grid;
          grid-template-columns: repeat(2, minmax(0, 1fr));
          gap: 12px;
        }

        .tk-ticket-card {
          position: relative;
          min-width: 0;
          padding: 16px;
          overflow: hidden;
          border: 1px solid rgba(255,255,255,0.10);
          border-radius: 20px;
          background:
            linear-gradient(
              145deg,
              rgba(255,255,255,0.072),
              rgba(255,255,255,0.029)
            );
          box-shadow:
            inset 0 1px 0 rgba(255,255,255,0.10),
            0 12px 30px rgba(0,0,0,0.10);
          transition:
            border-color 0.22s ease,
            background 0.22s ease;
        }

        .tk-ticket-card::before {
          content: "";
          position: absolute;
          top: 0;
          left: 8%;
          width: 45%;
          height: 1px;
          pointer-events: none;
          background:
            linear-gradient(
              90deg,
              rgba(255,255,255,0.30),
              transparent
            );
        }

        .tk-ticket-card:hover {
          border-color: rgba(255,255,255,0.17);
          background:
            linear-gradient(
              145deg,
              rgba(255,255,255,0.09),
              rgba(255,255,255,0.038)
            );
        }

        .tk-ticket-card.is-urgent {
          border-color: rgba(235,181,27,0.24);
          background:
            linear-gradient(
              145deg,
              rgba(235,181,27,0.075),
              rgba(255,255,255,0.03)
            );
        }

        .tk-ticket-topline {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 10px;
          margin-bottom: 12px;
        }

        .tk-ticket-id {
          display: flex;
          align-items: center;
          gap: 6px;
          min-width: 0;
          color: rgba(255,255,255,0.32);
          font-size: 8px;
          font-weight: 750;
          letter-spacing: 0.5px;
        }

        .tk-badges {
          display: flex;
          align-items: center;
          justify-content: flex-end;
          flex-wrap: wrap;
          gap: 5px;
        }

        .tk-badge {
          display: inline-flex;
          align-items: center;
          gap: 4px;
          min-height: 23px;
          padding: 0 7px;
          border-radius: 999px;
          font-size: 8px;
          font-weight: 800;
          white-space: nowrap;
        }

        .tk-badge-urgent {
          color: ${GOLD};
          border: 1px solid rgba(235,181,27,0.23);
          background: rgba(235,181,27,0.08);
        }

        .tk-status {
          color: rgba(255,255,255,0.62);
          border: 1px solid rgba(255,255,255,0.10);
          background: rgba(255,255,255,0.045);
        }

        .tk-status-in_lavorazione {
          color: rgba(255,221,120,0.82);
          border-color: rgba(235,181,27,0.15);
          background: rgba(235,181,27,0.05);
        }

        .tk-status-chiuso {
          color: rgba(165,225,180,0.78);
          border-color: rgba(100,190,120,0.16);
          background: rgba(60,155,85,0.07);
        }

        .tk-ticket-card h3 {
          margin: 0 0 7px;
          color: rgba(255,255,255,0.90);
          font-size: 14px;
          font-weight: 700;
          line-height: 1.35;
          letter-spacing: -0.2px;
        }

        .tk-ticket-description {
          margin: 0;
          color: rgba(255,255,255,0.50);
          font-size: 10px;
          line-height: 1.65;
          white-space: pre-wrap;
          overflow-wrap: anywhere;
        }

        .tk-image-preview {
          position: relative;
          display: block;
          width: 100%;
          height: 130px;
          margin-top: 12px;
          padding: 0;
          overflow: hidden;
          border: 1px solid rgba(255,255,255,0.10);
          border-radius: 15px;
          background: rgba(0,0,0,0.13);
          cursor: pointer;
        }

        .tk-image-preview img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          opacity: 0.82;
          transition: opacity 0.2s ease;
        }

        .tk-image-preview:hover img {
          opacity: 0.98;
        }

        .tk-image-preview span {
          position: absolute;
          right: 8px;
          bottom: 8px;
          display: flex;
          align-items: center;
          gap: 5px;
          padding: 5px 8px;
          border: 1px solid rgba(255,255,255,0.12);
          border-radius: 999px;
          color: rgba(255,255,255,0.84);
          background: rgba(20,21,24,0.65);
          backdrop-filter: blur(12px);
          -webkit-backdrop-filter: blur(12px);
          font-size: 8px;
          font-weight: 700;
        }

        .tk-ticket-meta {
          display: flex;
          align-items: center;
          flex-wrap: wrap;
          gap: 8px 13px;
          margin-top: 14px;
          padding-top: 12px;
          border-top: 1px solid rgba(255,255,255,0.07);
        }

        .tk-ticket-meta > div {
          display: flex;
          align-items: center;
          gap: 5px;
          min-width: 0;
          color: rgba(255,255,255,0.32);
          font-size: 8px;
        }

        .tk-ticket-meta b {
          color: rgba(255,255,255,0.57);
          font-weight: 700;
        }

        .tk-status-editor {
          margin-top: 13px;
          padding-top: 12px;
          border-top: 1px solid rgba(255,255,255,0.07);
        }

        .tk-status-editor > span {
          display: block;
          margin-bottom: 7px;
          color: rgba(255,255,255,0.32);
          font-size: 8px;
          font-weight: 800;
          letter-spacing: 1px;
          text-transform: uppercase;
        }

        .tk-status-buttons {
          display: flex;
          flex-wrap: wrap;
          gap: 6px;
        }

        .tk-status-choice {
          display: inline-flex;
          align-items: center;
          gap: 5px;
          min-height: 31px;
          padding: 0 9px;
          border: 1px solid rgba(255,255,255,0.09);
          border-radius: 10px;
          color: rgba(255,255,255,0.43);
          background: rgba(255,255,255,0.035);
          font-size: 8px;
          font-weight: 700;
          cursor: pointer;
          transition:
            color 0.2s ease,
            border-color 0.2s ease,
            background 0.2s ease;
        }

        .tk-status-choice:hover {
          color: rgba(255,255,255,0.75);
          border-color: rgba(255,255,255,0.16);
          background: rgba(255,255,255,0.06);
        }

        .tk-status-choice.is-active {
          color: ${GOLD};
          border-color: rgba(235,181,27,0.25);
          background: rgba(235,181,27,0.075);
        }

        .tk-last-update {
          margin-top: 9px;
          color: rgba(255,255,255,0.27);
          font-size: 7.5px;
        }

        .tk-last-update b {
          color: rgba(255,255,255,0.45);
          font-weight: 650;
        }

        .tk-empty {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          min-height: 160px;
          padding: 25px;
          border: 1px dashed rgba(255,255,255,0.10);
          border-radius: 18px;
          color: rgba(255,255,255,0.22);
          text-align: center;
        }

        .tk-empty strong {
          margin-top: 10px;
          color: rgba(255,255,255,0.50);
          font-size: 11px;
        }

        .tk-empty span {
          max-width: 430px;
          margin-top: 4px;
          color: rgba(255,255,255,0.30);
          font-size: 9px;
          line-height: 1.5;
        }

        .tk-compose-layout {
          display: grid;
          grid-template-columns: minmax(0, 1fr) 285px;
          gap: 16px;
          align-items: start;
        }

        .tk-compose-card,
        .tk-compose-side {
          border: 1px solid rgba(255,255,255,0.13);
          background:
            linear-gradient(
              145deg,
              rgba(255,255,255,0.09),
              rgba(255,255,255,0.034)
            );
          box-shadow:
            0 24px 65px rgba(0,0,0,0.19),
            inset 0 1px 0 rgba(255,255,255,0.14);
          backdrop-filter: blur(27px) saturate(135%);
          -webkit-backdrop-filter: blur(27px) saturate(135%);
        }

        .tk-compose-card {
          padding: 24px;
          border-radius: 27px;
        }

        .tk-compose-header {
          display: flex;
          align-items: center;
          gap: 14px;
          margin-bottom: 22px;
        }

        .tk-compose-icon {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 54px;
          height: 54px;
          flex: 0 0 auto;
          border: 1px solid rgba(235,181,27,0.24);
          border-radius: 17px;
          color: ${GOLD};
          background:
            linear-gradient(
              145deg,
              rgba(235,181,27,0.15),
              rgba(235,181,27,0.045)
            );
          box-shadow: inset 0 1px 0 rgba(255,255,255,0.12);
        }

        .tk-compose-header span {
          color: rgba(255,255,255,0.31);
          font-size: 8px;
          font-weight: 800;
          letter-spacing: 1.4px;
          text-transform: uppercase;
        }

        .tk-compose-header h2 {
          margin: 1px 0 0;
          color: #f7f7f8;
          font-size: 21px;
          font-weight: 650;
          letter-spacing: -0.5px;
        }

        .tk-compose-header p {
          margin: 3px 0 0;
          color: rgba(255,255,255,0.38);
          font-size: 9px;
        }

        .tk-form-grid {
          display: grid;
          grid-template-columns: repeat(2, minmax(0, 1fr));
          gap: 11px;
        }

        .tk-field {
          position: relative;
          display: block;
          margin-bottom: 13px;
        }

        .tk-field > span {
          display: flex;
          align-items: center;
          gap: 6px;
          margin: 0 0 7px 2px;
          color: rgba(255,255,255,0.47);
          font-size: 9px;
          font-weight: 700;
        }

        .tk-field input,
        .tk-field select,
        .tk-field textarea {
          width: 100%;
          outline: none;
          border: 1px solid rgba(255,255,255,0.10);
          border-radius: 14px;
          color: rgba(255,255,255,0.86);
          background: rgba(255,255,255,0.045);
          box-shadow: inset 0 1px 0 rgba(255,255,255,0.06);
          font: inherit;
          font-size: 11px;
          transition:
            border-color 0.2s ease,
            background 0.2s ease,
            box-shadow 0.2s ease;
        }

        .tk-field input,
        .tk-field select {
          height: 45px;
          padding: 0 13px;
        }

        .tk-field textarea {
          min-height: 145px;
          resize: vertical;
          padding: 12px 13px 25px;
          line-height: 1.6;
        }

        .tk-field select option {
          color: #f7f7f8;
          background: #25272b;
        }

        .tk-field input::placeholder,
        .tk-field textarea::placeholder {
          color: rgba(255,255,255,0.24);
        }

        .tk-field input:focus,
        .tk-field select:focus,
        .tk-field textarea:focus {
          border-color: rgba(235,181,27,0.36);
          background: rgba(255,255,255,0.065);
          box-shadow:
            0 0 0 3px rgba(235,181,27,0.045),
            inset 0 1px 0 rgba(255,255,255,0.08);
        }

        .tk-field select:disabled {
          opacity: 0.45;
          cursor: not-allowed;
        }

        .tk-field > small {
          position: absolute;
          right: 9px;
          bottom: 7px;
          color: rgba(255,255,255,0.21);
          font-size: 7px;
        }

        .tk-compose-options {
          display: grid;
          grid-template-columns: repeat(2, minmax(0, 1fr));
          gap: 10px;
          margin-top: 2px;
        }

        .tk-urgent-toggle,
        .tk-attachment {
          display: flex;
          align-items: center;
          gap: 10px;
          min-height: 65px;
          padding: 11px;
          border: 1px solid rgba(255,255,255,0.09);
          border-radius: 15px;
          background: rgba(255,255,255,0.034);
          cursor: pointer;
        }

        .tk-urgent-toggle > input,
        .tk-attachment > input {
          position: absolute;
          opacity: 0;
          pointer-events: none;
        }

        .tk-switch {
          position: relative;
          width: 36px;
          height: 21px;
          flex: 0 0 auto;
          border: 1px solid rgba(255,255,255,0.12);
          border-radius: 999px;
          background: rgba(255,255,255,0.07);
          transition:
            background 0.2s ease,
            border-color 0.2s ease;
        }

        .tk-switch::after {
          content: "";
          position: absolute;
          top: 3px;
          left: 3px;
          width: 13px;
          height: 13px;
          border-radius: 50%;
          background: rgba(255,255,255,0.62);
          transition:
            transform 0.2s ease,
            background 0.2s ease;
        }

        .tk-urgent-toggle > input:checked + .tk-switch {
          border-color: rgba(235,181,27,0.35);
          background: rgba(235,181,27,0.16);
        }

        .tk-urgent-toggle > input:checked + .tk-switch::after {
          transform: translateX(15px);
          background: ${GOLD};
        }

        .tk-urgent-toggle > div,
        .tk-attachment > div {
          min-width: 0;
          display: flex;
          flex-direction: column;
        }

        .tk-urgent-toggle b,
        .tk-attachment b {
          display: flex;
          align-items: center;
          gap: 5px;
          overflow: hidden;
          color: rgba(255,255,255,0.68);
          font-size: 9px;
          font-weight: 700;
          white-space: nowrap;
          text-overflow: ellipsis;
        }

        .tk-urgent-toggle small,
        .tk-attachment small {
          margin-top: 2px;
          color: rgba(255,255,255,0.27);
          font-size: 7.5px;
          line-height: 1.35;
        }

        .tk-attachment > svg {
          flex: 0 0 auto;
          color: ${GOLD};
        }

        .tk-compose-image {
          position: relative;
          width: 180px;
          height: 112px;
          margin-top: 12px;
          overflow: hidden;
          border: 1px solid rgba(255,255,255,0.11);
          border-radius: 15px;
          background: rgba(0,0,0,0.15);
        }

        .tk-compose-image img {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }

        .tk-compose-image button {
          position: absolute;
          top: 7px;
          right: 7px;
          display: flex;
          align-items: center;
          justify-content: center;
          width: 29px;
          height: 29px;
          padding: 0;
          border: 1px solid rgba(255,255,255,0.14);
          border-radius: 10px;
          color: #fff;
          background: rgba(20,20,23,0.72);
          cursor: pointer;
        }

        .tk-form-actions {
          display: flex;
          align-items: center;
          justify-content: flex-end;
          gap: 8px;
          margin-top: 21px;
          padding-top: 17px;
          border-top: 1px solid rgba(255,255,255,0.07);
        }

        .tk-btn-primary,
        .tk-btn-secondary {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 7px;
          min-height: 40px;
          padding: 0 15px;
          border-radius: 999px;
          font-size: 9px;
          font-weight: 750;
          cursor: pointer;
          transition:
            background 0.2s ease,
            border-color 0.2s ease,
            color 0.2s ease;
        }

        .tk-btn-secondary {
          border: 1px solid rgba(255,255,255,0.11);
          color: rgba(255,255,255,0.46);
          background: rgba(255,255,255,0.035);
        }

        .tk-btn-primary {
          border: 1px solid rgba(235,181,27,0.44);
          color: ${GOLD};
          background: rgba(235,181,27,0.08);
        }

        .tk-btn-secondary:hover {
          color: rgba(255,255,255,0.76);
          background: rgba(255,255,255,0.06);
        }

        .tk-btn-primary:hover {
          color: #fff;
          border-color: ${GOLD};
          background: rgba(235,181,27,0.14);
        }

        .tk-btn-primary:disabled {
          opacity: 0.45;
          cursor: wait;
        }

        .tk-compose-side {
          position: sticky;
          top: 20px;
          padding: 19px;
          border-radius: 23px;
        }

        .tk-compose-side > span {
          display: block;
          margin-bottom: 12px;
          color: rgba(255,255,255,0.34);
          font-size: 8px;
          font-weight: 800;
          letter-spacing: 1.3px;
          text-transform: uppercase;
        }

        .tk-summary-row {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 11px 0;
          border-bottom: 1px solid rgba(255,255,255,0.065);
        }

        .tk-summary-row:last-child {
          border-bottom: 0;
        }

        .tk-summary-row > svg {
          flex: 0 0 auto;
          color: ${GOLD};
        }

        .tk-summary-row > div {
          min-width: 0;
          display: flex;
          flex-direction: column;
        }

        .tk-summary-row small {
          color: rgba(255,255,255,0.27);
          font-size: 7.5px;
        }

        .tk-summary-row strong {
          margin-top: 2px;
          overflow: hidden;
          color: rgba(255,255,255,0.65);
          font-size: 9px;
          font-weight: 700;
          white-space: nowrap;
          text-overflow: ellipsis;
        }

        .tk-stats-inline {
          display: flex;
          align-items: center;
          justify-content: flex-end;
          flex-wrap: wrap;
          gap: 6px;
        }

        .tk-stats-inline span {
          display: inline-flex;
          align-items: center;
          gap: 5px;
          min-height: 31px;
          padding: 0 9px;
          border: 1px solid rgba(255,255,255,0.09);
          border-radius: 10px;
          color: rgba(255,255,255,0.42);
          background: rgba(255,255,255,0.035);
          font-size: 8px;
          white-space: nowrap;
        }

        .tk-reply-box {
          margin-top: 16px;
          padding: 14px;
          border: 1px solid rgba(235,181,27,0.18);
          border-radius: 15px;
          background: rgba(235,181,27,0.055);
        }

        .tk-reply-heading {
          display: flex;
          align-items: center;
          gap: 7px;
          color: ${GOLD};
          font-size: 12px;
        }

        .tk-reply-box p {
          margin: 10px 0 0;
          color: rgba(255,255,255,0.84);
          font-size: 13px;
          line-height: 1.65;
          white-space: pre-wrap;
          overflow-wrap: anywhere;
        }

        .tk-reply-meta {
          margin-top: 8px;
          color: rgba(255,255,255,0.46);
          font-size: 11px;
        }

        .tk-reply-attachment-button,
        .tk-reply-toggle {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 7px;
          min-height: 38px;
          margin-top: 10px;
          padding: 0 12px;
          border: 1px solid rgba(255,255,255,0.13);
          border-radius: 11px;
          color: rgba(255,255,255,0.78);
          background: rgba(255,255,255,0.05);
          font-size: 11px;
          font-weight: 750;
          cursor: pointer;
        }

        .tk-reply-editor {
          margin-top: 14px;
          padding-top: 13px;
          border-top: 1px solid rgba(255,255,255,0.07);
        }

        .tk-reply-form {
          display: grid;
          gap: 10px;
          margin-top: 10px;
        }

        .tk-reply-form textarea {
          width: 100%;
          min-height: 110px;
          resize: vertical;
          padding: 12px 13px;
          border: 1px solid rgba(255,255,255,0.12);
          border-radius: 13px;
          outline: none;
          color: #fff;
          background: rgba(0,0,0,0.16);
          font: inherit;
          font-size: 13px;
          line-height: 1.55;
        }

        .tk-reply-actions-row {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 10px;
          flex-wrap: wrap;
        }

        .tk-reply-file {
          position: relative;
          display: inline-flex;
          align-items: center;
          gap: 7px;
          min-height: 38px;
          padding: 0 12px;
          border: 1px solid rgba(255,255,255,0.11);
          border-radius: 11px;
          color: rgba(255,255,255,0.70);
          background: rgba(255,255,255,0.04);
          font-size: 11px;
          cursor: pointer;
          overflow: hidden;
        }

        .tk-reply-file input {
          position: absolute;
          inset: 0;
          opacity: 0;
          cursor: pointer;
        }

        .tk-reply-save {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 7px;
          min-height: 38px;
          padding: 0 13px;
          border: 1px solid rgba(235,181,27,0.28);
          border-radius: 11px;
          color: #202227;
          background: ${GOLD};
          font-size: 11px;
          font-weight: 850;
          cursor: pointer;
        }

        .tk-reply-save:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .tk-reply-local-preview {
          position: relative;
          width: 190px;
          height: 120px;
          overflow: hidden;
          border-radius: 13px;
          border: 1px solid rgba(255,255,255,0.11);
        }

        .tk-reply-local-preview img {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }

        .tk-reply-local-preview button {
          position: absolute;
          top: 7px;
          right: 7px;
          display: grid;
          place-items: center;
          width: 28px;
          height: 28px;
          padding: 0;
          border: 1px solid rgba(255,255,255,0.15);
          border-radius: 9px;
          color: #fff;
          background: rgba(15,16,18,0.75);
          cursor: pointer;
        }

        /* Leggibilità: dimensioni minime più comode su desktop e mobile. */
        .tk-eyebrow { font-size: 11px; }
        .tk-user-glass span { font-size: 10px; }
        .tk-user-glass strong { font-size: 14px; }
        .tk-user-glass small { font-size: 11px; }
        .tk-warning-banner, .tk-notice, .tk-error { font-size: 12px; }
        .tk-tab { font-size: 12px; min-height: 46px; }
        .tk-tab b { font-size: 10px; }
        .tk-section-kicker { font-size: 10px; }
        .tk-section-heading p { font-size: 12px; }
        .tk-ticket-id { font-size: 11px; }
        .tk-badge { font-size: 10px; }
        .tk-ticket-card h3 { font-size: 17px; line-height: 1.3; }
        .tk-ticket-description { font-size: 13px; line-height: 1.7; }
        .tk-image-preview span { font-size: 10px; }
        .tk-ticket-meta > div { font-size: 11px; }
        .tk-status-editor > span { font-size: 10px; }
        .tk-status-choice { font-size: 11px; min-height: 36px; padding: 0 11px; }
        .tk-last-update { font-size: 11px; }
        .tk-compose-header span { font-size: 10px; }
        .tk-compose-header p { font-size: 12px; line-height: 1.5; }
        .tk-field > span { font-size: 11px; }
        .tk-field input, .tk-field select, .tk-field textarea { font-size: 13px; }
        .tk-btn-primary, .tk-btn-secondary { font-size: 11px; min-height: 44px; }
        .tk-compose-side > span { font-size: 10px; }
        .tk-urgent-toggle b, .tk-attachment b { font-size: 12px; }
        .tk-urgent-toggle small, .tk-attachment small { font-size: 10px; }
        .tk-summary-row small { font-size: 10px; }
        .tk-summary-row strong { font-size: 11px; }
        .tk-stats-inline span { font-size: 10px; }

        .tk-image-modal {
          position: fixed;
          top: 0;
          right: 0;
          bottom: 0;
          left: 0;
          width: 100%;
          height: 100vh;
          max-width: 100vw;
          max-height: 100vh;
          z-index: 2147483647;
          display: grid;
          place-items: center;
          margin: 0;
          padding: clamp(10px, 2vw, 24px);
          overflow: hidden;
          overscroll-behavior: none;
          box-sizing: border-box;
          background: rgba(8,9,12,0.78);
          backdrop-filter: blur(26px) saturate(125%);
          -webkit-backdrop-filter: blur(26px) saturate(125%);
        }

        .tk-image-modal-card {
          position: relative;
          display: flex;
          flex-direction: column;
          align-items: stretch;
          justify-content: center;
          width: min(900px, calc(100vw - 32px));
          max-width: calc(100vw - 32px);
          max-height: calc(100vh - 32px);
          margin: 0;
          padding: 14px;
          overflow: hidden;
          box-sizing: border-box;
          border: 1px solid rgba(255,255,255,0.17);
          border-radius: 25px;
          background:
            linear-gradient(
              145deg,
              rgba(255,255,255,0.13),
              rgba(255,255,255,0.055)
            );
          box-shadow:
            0 45px 120px rgba(0,0,0,0.52),
            inset 0 1px 0 rgba(255,255,255,0.20);
          backdrop-filter: blur(32px) saturate(145%);
          -webkit-backdrop-filter: blur(32px) saturate(145%);
        }

        .tk-image-modal-card > img {
          display: block;
          width: auto;
          height: auto;
          max-width: 100%;
          max-height: calc(100vh - 142px);
          margin: 0 auto;
          flex: 0 1 auto;
          min-height: 0;
          object-fit: contain;
          border-radius: 17px;
          background: rgba(0,0,0,0.19);
        }

        .tk-image-close {
          position: absolute;
          top: 21px;
          right: 21px;
          z-index: 2;
          display: flex;
          align-items: center;
          justify-content: center;
          width: 36px;
          height: 36px;
          border: 1px solid rgba(255,255,255,0.15);
          border-radius: 12px;
          color: #fff;
          background: rgba(15,16,18,0.65);
          cursor: pointer;
          backdrop-filter: blur(12px);
          -webkit-backdrop-filter: blur(12px);
        }

        .tk-image-modal-footer {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 12px;
          min-width: 0;
          padding: 10px 2px 0;
        }

        .tk-image-caption {
          min-width: 0;
          overflow: hidden;
          color: rgba(255,255,255,0.44);
          font-size: 9px;
          white-space: nowrap;
          text-overflow: ellipsis;
        }

        .tk-image-download {
          flex: 0 0 auto;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 7px;
          min-height: 38px;
          padding: 0 13px;
          border: 1px solid rgba(235,181,27,0.28);
          border-radius: 12px;
          color: ${GOLD};
          background: rgba(235,181,27,0.09);
          font-size: 10px;
          font-weight: 800;
          cursor: pointer;
          white-space: nowrap;
        }

        .tk-image-download:hover:not(:disabled) {
          background: rgba(235,181,27,0.15);
        }

        .tk-image-download:disabled {
          opacity: 0.55;
          cursor: wait;
        }

        @media (max-width: 1000px) {
          .tk-ticket-grid {
            grid-template-columns: 1fr;
          }

          .tk-compose-layout {
            grid-template-columns: 1fr;
          }

          .tk-compose-side {
            position: static;
            display: grid;
            grid-template-columns: repeat(2, minmax(0, 1fr));
            gap: 0 18px;
          }

          .tk-compose-side > span {
            grid-column: 1 / -1;
          }
        }

        @media (max-width: 760px) {
          .tk-page {
            width: min(100% - 24px, 1420px);
            padding-top: 25px;
          }

          .tk-hero {
            align-items: flex-start;
            flex-direction: column;
          }

          .tk-user-glass {
            width: 100%;
            min-width: 0;
          }

          .tk-hero h1 {
            font-size: 43px;
            letter-spacing: -2px;
          }

          .tk-tabs {
            width: 100%;
          }

          .tk-tab {
            flex: 1 0 auto;
          }

          .tk-section,
          .tk-compose-card {
            padding: 17px;
            border-radius: 22px;
          }

          .tk-section-heading {
            flex-direction: column;
          }

          .tk-form-grid,
          .tk-compose-options {
            grid-template-columns: 1fr;
          }

          .tk-compose-side {
            grid-template-columns: 1fr;
          }

          .tk-compose-side > span {
            grid-column: auto;
          }

          .tk-stats-inline {
            justify-content: flex-start;
          }
        }

        @media (max-width: 470px) {
          .tk-page {
            width: min(100% - 18px, 1420px);
          }

          .tk-ticket-topline {
            align-items: flex-start;
            flex-direction: column;
          }

          .tk-badges {
            justify-content: flex-start;
          }

          .tk-compose-header {
            align-items: flex-start;
          }

          .tk-form-actions {
            align-items: stretch;
            flex-direction: column-reverse;
          }

          .tk-btn-primary,
          .tk-btn-secondary {
            width: 100%;
          }

          .tk-image-modal {
            width: 100%;
            height: 100vh;
            padding: 10px;
          }

          .tk-image-modal-card {
            width: calc(100vw - 20px);
            max-width: calc(100vw - 20px);
            max-height: calc(100vh - 20px);
            padding: 10px;
            border-radius: 18px;
          }

          .tk-image-modal-card > img {
            max-height: calc(100vh - 124px);
            border-radius: 13px;
          }

          .tk-image-close {
            top: 16px;
            right: 16px;
          }

          .tk-image-modal-footer {
            gap: 8px;
          }

          .tk-image-download {
            min-height: 36px;
            padding: 0 10px;
            font-size: 9px;
          }
        }

        @media (prefers-reduced-motion: reduce) {
          .tk-tab-panel {
            animation: none !important;
          }

          .tk-ticket-card,
          .tk-tab,
          .tk-status-choice,
          .tk-btn-primary,
          .tk-btn-secondary,
          .tk-switch,
          .tk-switch::after {
            transition: none !important;
          }
        }

        
      `}</style>
    </div>
  );
}

export default TicketPage;